import _ from "lodash";
import moment from "moment";
import { formatAssetInfoTradingHours } from "./utils/formatAssetInfoTradingHours";
import { LITE_TRADE_POSITION_SIDE, TLiteTradePositionSide } from "../LiteTrade/liteTradeConstants";
import { postLiteTradeRequest } from "../LiteTrade/liteTradeRequest";
import { getTimeBeforeBidding } from './utils/getTimeBeforeBidding';
import type {
    ILiteTradeAsset,
    ILiteTradeAssetInfo,
    ILiteTradeAssetSymbol,
    ILiteTradeMarketItem,
    ILiteTradePortfolioPosition,
} from "../LiteTrade/types/liteTradeReducer.t";
import type { IAccountCode } from "../../app/types/appReducer.t";
import type {
    IAssetsParams,
    IPostLiteTradeRequestResult,
    IPrice,
    ILotsType,
    AssetFilledQuantity
} from "./Asset.t";
import { IAssetWeekDaySchedule } from "./Asset.t";
import { debugLog } from "../../debug";

class Asset {
    asset: ILiteTradeAsset;
    info: ILiteTradeAssetInfo;
    crossConversationQuoteForUsedMargin: number | null;
    symbol: ILiteTradeAssetSymbol;
    crossConversationQuoteForPl: number | null;
    assets: ILiteTradeAsset[];
    selectedAccountCode?: IAccountCode;
    ready: boolean;

    constructor(asset: ILiteTradeAsset, params: IAssetsParams = {}) {
        this.asset = asset;
        this.info = asset.info || {} as ILiteTradeAssetInfo;
        this.symbol = asset.symbol;
        this.crossConversationQuoteForUsedMargin = null;
        this.crossConversationQuoteForPl = null;
        this.selectedAccountCode = params?.selectedAccountCode;
        //@ts-ignore  ?to fix:  this.assets = params?.assets || []; 
        this.assets = params?.assets; // for search conversation quote
        this.ready = false;
        if (this.assets && this.selectedAccountCode && this.asset.symbol.indexOf('USD') === -1) {
            this.searchCrossConversationQuote();
        } else {
            this.crossConversationQuoteForUsedMargin = 1;
            this.crossConversationQuoteForPl = 1;
            this.ready = true;
        }
    }

    isReadyPromiseExecutor(attempt: number, timeout: number | undefined) {
        return (resolve: (value?: unknown) => void, reject: () => void) => {
            if (this.ready || attempt <= 0) {
                return resolve();
            }
            setTimeout(() => {
                return this.isReadyPromiseExecutor(attempt - 1, timeout)(resolve, reject);
            }, timeout);
        }
    }

    async isReady(attempt = 10, timeout = 300) {
        return new Promise(this.isReadyPromiseExecutor(attempt, timeout));
    }

    searchCrossConversationQuote() {
        const symbol1 = this.asset.info?.firstCurrency; // базовая
        const symbol2 = this.asset.info?.currency;
        const pairs = [
            `USD${symbol1}`,
            `${symbol1}USD`,
            `USD${symbol2}`,
            `${symbol2}USD`,
        ];
        const list = _.filter(this.assets, item => pairs.includes(item.symbol));
        if (!list.length) {
            this.ready = true;
            return;
        }
        const crossAssets = list.map(item => item.symbol);
        const params = {
            account: `default:${this.selectedAccountCode}`,
            symbols: crossAssets,
            eventTypes: [
                {
                    type: "Quote",
                    format: "COMPACT",
                }
            ]
        };

        return postLiteTradeRequest<IPostLiteTradeRequestResult>('/marketdata', params).then((res: IPostLiteTradeRequestResult) => {
            res.events.forEach(asset => {
                const quote = asset.symbol.indexOf('USD') === 0 ? (1 / asset.bid) : asset.bid;
                //@ts-ignore
                if (asset.symbol.indexOf(symbol1) !== -1) {
                    this.crossConversationQuoteForUsedMargin = quote;
                } else {
                    this.crossConversationQuoteForPl = quote;
                    if (['FTSGBP', 'NIKJPY', 'DAXEUR'].includes(this.asset.symbol)) {
                        this.crossConversationQuoteForUsedMargin = quote;
                    }
                }
            });

            // TODO temporary
            debugLog('Asset', '[Asset] postLiteTradeRequest crossConversationQuote for', this.asset.symbol, ': for usedMargin -', this.crossConversationQuoteForUsedMargin, ', for PL -', this.crossConversationQuoteForPl);
        })
            .catch((e: any) => {
                debugLog('Asset', '[Asset] postLiteTradeRequest', e);
            })
            .then(() => {
                this.ready = true;
            });
    }

    getPositionPL(position: ILiteTradePortfolioPosition, price: IPrice) {
        //@ts-ignore
        const diff = (position.openPrice - price) * (position.side === LITE_TRADE_POSITION_SIDE.SELL ? 1 : -1);
        //@ts-ignore
        return this.getAssetPL(position?.quantity, price, diff);
    }

    getAssetPL(quantity: number, price: IPrice, diff: number) {
        if (this.asset.info?.currency === 'USD') {
            return quantity * diff;
        }
        const idx = this.asset.symbol.indexOf('USD');

        if (idx === -1) {
            return this.crossConversationQuoteForPl === null
                ? null
                : diff * quantity * this.crossConversationQuoteForPl;
        } else if (idx === 0) {
            return quantity * diff / price;
        } else {
            return quantity * diff;
        }
    }

    getAssetPriceFromPL(quantity: number, pl: number, openPrice: number, side: TLiteTradePositionSide) {
        const direction = side === LITE_TRADE_POSITION_SIDE.SELL ? 1 : -1;
        if (this.asset.info?.currency === 'USD') {
            return (quantity * openPrice * direction - pl) / (quantity * direction);
        }

        const idx = this.asset.symbol.indexOf('USD');
        if (idx === 0) {
            return quantity * openPrice * direction / (pl + quantity * direction);
        }
        if (idx !== -1) {
            return (quantity * openPrice * direction - pl) / (quantity * direction);
        }

        const crossConversationQuoteForPl = this.crossConversationQuoteForPl;

        if (crossConversationQuoteForPl === null) {
            return null;
        }

        return (openPrice * quantity * crossConversationQuoteForPl * direction - pl) / (quantity * crossConversationQuoteForPl * direction);
    }

    getAssetPriceFromPLAndTradeMarket(
        lots: number,
        pl: number,
        market: ILiteTradeMarketItem,
        side: TLiteTradePositionSide,
    ) {
        const quantity = _.round(this.roundLots(lots || 0.01) * this.getLotSize(), 2);
        const openPrice = market[side === LITE_TRADE_POSITION_SIDE.BUY ? 'ask' : 'bid'];
        const closePrice = this.getAssetPriceFromPL(quantity, pl, openPrice, side);
        return closePrice;
    }

    getPositionPriceFromPL(position: ILiteTradePortfolioPosition, pl: number) {
        return this.getAssetPriceFromPL(position?.quantity, pl, position.openPrice, position.side);
    }


    getConversationQuote(price: IPrice) {
        if (this.asset.info?.currency === 'USD') {
            return price;
        }
        if (['FTSGBP', 'NIKJPY', 'DAXEUR'].includes(this.asset.symbol)) {
            //@ts-ignore Object is possibly 'null'.
            return this.crossConversationQuoteForUsedMargin * price;
        }

        const idx = this.asset.symbol.indexOf('USD');
        if (idx === -1) {
            return this.crossConversationQuoteForUsedMargin || price;
        } else if (idx === 0) {
            return 1;
        } else {
            return price;
        }
    }

    getAmountDecimals() {
        //@ts-ignore
        return -Math.log10(this.getPriceIncrement());
    }

    roundAmount(amount: number) {
        return _.round(amount, this.getAmountDecimals());
    }

    amountToFixed(amount: number, withSign = false) {
        const sign = (!withSign || amount <= 0) ? '' : '+';
        return `${sign}${this.roundAmount(amount).toFixed(this.getAmountDecimals())}`;
    }

    roundLots(lots: number | null, type: ILotsType = 'round') {
        // my fix for null value of lots uncmment if correct or delete 
        // if (lots === null) {
        //     return lots;
        // }
        switch (type) {
            case 'ceil':
                //@ts-ignore  Type 'null' is not assignable to type 'number'.
                return _.ceil(lots, -Math.log10(this.getMinOrderSizeIncrement()));
            case 'floor':
                //@ts-ignore Type 'null' is not assignable to type 'number'.
                return _.floor(lots, -Math.log10(this.getMinOrderSizeIncrement()));
            default:
                //@ts-ignore  Type 'null' is not assignable to type 'number'.
                return _.round(lots, -Math.log10(this.getMinOrderSizeIncrement()));
        }
    }

    lotsToAmount(lots: number, price: IPrice, side?: TLiteTradePositionSide, filledQuantity: AssetFilledQuantity = {
        [LITE_TRADE_POSITION_SIDE.BUY]: 0,
        [LITE_TRADE_POSITION_SIDE.SELL]: 0
    }, spread = 0) {
        if (!price) return null;
        if (lots <= 0) return 0;

        const lotSize = this.getLotSize();
        const conversationQuote = this.getConversationQuote(price);
        if (conversationQuote === null) return null;

        const tiers = this.asset.marginRate.tiers;
        let resultAmount = 0;
        let prevEndVolume = 0;

        const usedQuantity = _.max(Object.values(filledQuantity));
        //@ts-ignore Type 'undefined' cannot be used as an index type.t
        const fillQuantity = (filledQuantity[side] || 0);

        let skipQuantity = usedQuantity;
        //@ts-ignore 'usedQuantity' is possibly 'undefined'.
        let leftQuantity = lots * lotSize + fillQuantity - usedQuantity;

        tiers.forEach(tier => {
            if (leftQuantity <= 0) return;
            let tierQuantity = tier.endVolume - prevEndVolume;
            //@ts-ignore   This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.ts(2367)
            if (tier.endVolume === 'Infinity' || (skipQuantity + leftQuantity - tierQuantity) < 0) {
                resultAmount += leftQuantity * tier.value * conversationQuote;
                leftQuantity = 0;
            } else {
                //@ts-ignore 'skipQuantity' is possibly 'undefined'.
                if (skipQuantity >= tierQuantity) {
                    //@ts-ignore 'skipQuantity' is possibly 'undefined'.
                    skipQuantity -= tierQuantity;
                    tierQuantity = 0;
                } else {
                    //@ts-ignore 'skipQuantity' is possibly 'undefined'.
                    tierQuantity -= skipQuantity;
                    skipQuantity = 0;
                }
                if (tierQuantity > 0) {
                    resultAmount += tierQuantity * tier.value * conversationQuote;
                    leftQuantity -= tierQuantity;
                }
            }
            prevEndVolume = tier.endVolume;
        });
        //@ts-ignore
        return resultAmount + spread * lots * lotSize * conversationQuote;
    }

    amountToLots(value: number, price: IPrice, side?: TLiteTradePositionSide, filledQuantity: AssetFilledQuantity = {
        [LITE_TRADE_POSITION_SIDE.BUY]: 0,
        [LITE_TRADE_POSITION_SIDE.SELL]: 0
    }) {
        if (!price) return null;
        if (value <= 0) return 0;

        const lotSize = this.getLotSize();
        const conversationQuote = this.getConversationQuote(price);
        if (conversationQuote === null) return null;

        const tiers = this.asset.marginRate.tiers;
        let result = 0;
        let prevEndVolume = 0;
        let valueLeft = value;

        const usedQuantity = _.max(Object.values(filledQuantity));
        const fillQuantity = filledQuantity[side] || 0;
        let skipQuantity = usedQuantity;
        //@ts-ignore 'usedQuantity' is possibly 'undefined'.
        if (usedQuantity > fillQuantity) {
            //@ts-ignore 'usedQuantity' is possibly 'undefined'.
            result += (usedQuantity - fillQuantity) / lotSize;
        }

        tiers.forEach(tier => {
            if (valueLeft === 0) return;
            let tierQuantity = tier.endVolume - prevEndVolume;
            //@ts-ignore 'skipQuantity' is possibly 'undefined'.
            if ((tierQuantity - skipQuantity) <= 0) {
                //@ts-ignore 'skipQuantity' is possibly 'undefined'.
                skipQuantity -= tierQuantity;
            } else {
                //@ts-ignore 'skipQuantity' is possibly 'undefined'.
                tierQuantity -= skipQuantity;
                skipQuantity = 0;


                const totalTierAmount = tierQuantity * tier.value * conversationQuote;
                //@ts-ignore This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.
                if (tier.endVolume === 'Infinity' || (valueLeft - totalTierAmount) < 0) {
                    //@ts-ignore
                    result += this.roundLots(valueLeft / (lotSize * tier.value * conversationQuote), 'floor');
                    valueLeft = 0;
                } else {
                    //@ts-ignore
                    result += this.roundLots(tierQuantity / lotSize, 'floor');
                    valueLeft -= totalTierAmount;
                }
            }
            prevEndVolume = tier.endVolume;
        });
        return result;
    }

    getLotSize() {
        return this.asset.info?.lotSize;
    }

    getPipSize() {
        return this.asset.info?.pipSize;
    }

    priceShift(spread: number) {
        const pipSize = this.getPipSize();
        const multiplier = this.asset.type === 'CFD' ? 1000 : (this.asset.type === 'CFD_STOCK' ? 50 : 20);
        //@ts-ignore
        return _.max([spread * 20, pipSize * multiplier])
    }

    getPriceIncrement() {
        return this.asset.info?.priceIncrement;
    }

    getMinOrderSizeIncrement() {
        return this.asset.minOrderSizeIncrement;
    }

    getMinOrderSizeLots() {
        return this.asset.minOrderSize;
    }

    getMaxOrderSizeLots() {
        return this.asset.maxOrderSize.value;
    }

    getMinOrderSizeAmount(price: IPrice) {
        return this.roundLots(this.lotsToAmount(this.getMinOrderSizeLots(), price), 'ceil');
    }

    getMaxOrderSizeAmount(price: IPrice) {
        return this.roundLots(this.lotsToAmount(this.getMaxOrderSizeLots(), price), 'floor');
    }

    isTradingNow() {
        const week = formatAssetInfoTradingHours(this.asset.info?.tradingHours);
        const weekDay = moment().format("dddd");
        const day = _.find(week, { weekDay }) as IAssetWeekDaySchedule | undefined;
        if (!day) {
            return false;
        }
        const timeNow = moment().format("HH:mm");
        return day.intervals.some(interval => timeNow >= interval.startTime && timeNow <= interval.endTime);
    }

    timeBeforeBidding() {
        return getTimeBeforeBidding(this.asset.info?.tradingHours)
    }
}

export default Asset;
